import { ipdTaskWindowRule, type IpdTaskView, type IpdWardTaskWindow, type StaffDutyWard } from '@his/shared';
import { displayStatus, taskIsPending } from './record-task';

const MINUTE = 60_000;

export type DueTask = Pick<IpdTaskView, 'storedStatus' | 'scheduledAt' | 'taskKind'>;

export interface DueCounts {
  due: number;
  overdue: number;
}

export interface WardDueStrip extends DueCounts {
  ward: StaffDutyWard;
}

export function dueCounts(tasks: readonly DueTask[], nowMs: number): DueCounts {
  const counts: DueCounts = { due: 0, overdue: 0 };
  for (const task of tasks) {
    if (!taskIsPending(task)) continue;
    const status = displayStatus(task, nowMs);
    if (status === 'due') counts.due += 1;
    else if (status === 'overdue' || status === 'missed') counts.overdue += 1;
  }
  return counts;
}

export function nextDueChange(tasks: readonly DueTask[], nowMs: number): number | null {
  let next: number | null = null;
  for (const task of tasks) {
    if (!taskIsPending(task)) continue;
    const scheduled = Date.parse(task.scheduledAt);
    if (!Number.isFinite(scheduled)) continue;
    const rule = ipdTaskWindowRule(task.taskKind);
    for (const moment of [scheduled - rule.leadMinutes * MINUTE, scheduled + rule.graceMinutes * MINUTE]) {
      if (moment > nowMs && (next === null || moment < next)) next = moment;
    }
  }
  return next;
}

export function wardDueStrips(wards: readonly StaffDutyWard[], windowOf: (wardId: string) => Pick<IpdWardTaskWindow, 'data'> | null | undefined, nowMs: number): WardDueStrip[] {
  const strips: WardDueStrip[] = [];
  for (const ward of wards) {
    const window = windowOf(ward.id);
    if (!window) continue;
    const counts = dueCounts(window.data, nowMs);
    if (counts.due + counts.overdue > 0) strips.push({ ward, ...counts });
  }
  return strips;
}

export function totalDue(strips: readonly DueCounts[]): DueCounts {
  return strips.reduce<DueCounts>((total, strip) => ({ due: total.due + strip.due, overdue: total.overdue + strip.overdue }), { due: 0, overdue: 0 });
}
